import {
  CompassIcon,
  ClockIcon,
  ShuffleIcon,
  TrendingUpIcon,
  SearchIcon,
  ShieldQuestionIcon,
  FileTextIcon,
  ClipboardCheckIcon,
  MapIcon,
  Grid3x3Icon,
  CameraIcon,
} from "lucide-react";

export const CHECKOUT_URL = "https://lipo-en.impultienda.ar/checkout/";

export const images = {
  mockup: "https://cdn.magicpatterns.com/uploads/gPGgCAMD1G2vxsPPsQkiGZ/LIPO-EN.png",
  author: "https://cdn.magicpatterns.com/uploads/uEwNK1rvCjBj2TyL6t8oVs/MICHAEL_CARTER_-_IMPLANTES.png",
};

export const stages = ["Observe", "Analyze", "Prioritize", "Plan", "Review", "Learn"];

export const pains = [
  {
    icon: CompassIcon,
    title: "You look at a new case and don't know what to analyze first",
    text: "Multiple aesthetic variables, anatomical considerations and patient-specific factors compete for your attention.",
  },
  {
    icon: ClockIcon,
    title: "You spend too much time reviewing your notes",
    text: "Before discussing a case, you jump between articles, notes, lectures and previous cases.",
  },
  {
    icon: ShuffleIcon,
    title: "Every case feels different",
    text: "You have experience, but not always a consistent framework for comparing one case with another.",
  },
  {
    icon: TrendingUpIcon,
    title: "You want to improve your case analysis",
    text: "But consuming more information doesn't necessarily give you more opportunities to practice your reasoning.",
  },
  {
    icon: SearchIcon,
    title: "You want to learn from previous cases",
    text: "But you don't have a structured system for documenting what you noticed, what you missed and what you'd review again.",
  },
  {
    icon: ShieldQuestionIcon,
    title: "You want more confidence in your preoperative thinking",
    text: "Not because you need someone to decide for you — but because you want a better process for organizing the information in front of you.",
  },
];

export const volumes = [
  {
    numeral: "I",
    title: "The 15-Minute Case Analysis Framework",
    tagline: "Stop staring at a case wondering where to begin.",
    intro: "The structured framework I use to organize a case review into clear stages:",
    items: ["Case", "Observation", "Key Variables", "Priorities", "Questions", "Review"],
    flow: true,
    note: "Includes the 15-Minute Case Analysis Sheet so you can practice the framework repeatedly.",
    featured: false,
  },
  {
    numeral: "II",
    title: "The Body Contouring Analysis Map",
    tagline: "Learn to look at the case as a complete picture.",
    intro: "Organize your observations around the overall body-contouring context:",
    items: [
      "Patient profile",
      "Aesthetic objectives",
      "Relevant anatomical observations",
      "Treatment-area mapping",
      "Proportional considerations",
      "Areas requiring additional review",
      "Questions to clarify before planning",
      "Case documentation",
    ],
    flow: false,
    note: "Turn a complex visual case into an organized analysis.",
    featured: false,
  },
  {
    numeral: "III",
    title: "The Liposuction Case Planning Workbook",
    tagline: "Turn observations into a structured preoperative discussion.",
    intro: "A repeatable format for documenting your analysis before clinical decisions:",
    items: [
      "Case summary",
      "Patient objectives",
      "Areas of concern",
      "Key observations",
      "Questions to investigate",
      "Priorities",
      "Documentation prompts",
      "Risk-review prompts",
      "Planning notes",
      "Post-case reflection",
    ],
    flow: false,
    note: "Not a universal surgical recipe — a better place to organize your professional thinking.",
    featured: false,
  },
  {
    numeral: "IV",
    title: "The Body Contouring Case Atlas",
    tagline: "Study 20 educational cases instead of simply reading theory.",
    intro: "The heart of Liposuction Case Lab™. For each case you will:",
    items: [
      "Review the case",
      "Identify what you notice",
      "Write down your priorities",
      "Answer the analysis questions",
      "Compare your reasoning with the educational review",
      "Record what you learned",
    ],
    flow: false,
    note: "Transform passive consumption into active case practice.",
    featured: true,
  },
  {
    numeral: "V",
    title: "The Complication & Decision Review Atlas",
    tagline: "Review difficult scenarios before encountering them in your study.",
    intro: "Educational scenarios organized around issues that deserve additional review:",
    items: [
      "What happened?",
      "What information should be reviewed?",
      "What factors may require attention?",
      "What questions should be asked?",
      "What could have been documented better?",
      "What would you review differently?",
      "What lesson can be extracted?",
    ],
    flow: false,
    note: "A learning resource — not a substitute for institutional protocols, specialist training or clinical judgment.",
    featured: false,
  },
  {
    numeral: "VI",
    title: "The Surgeon's Case Review System",
    tagline: "Turn every case into another opportunity to learn.",
    intro: "Templates to build your own case-learning archive:",
    items: [
      "Case documentation",
      "Case reflection",
      "Case comparison",
      "Learning points",
      "Questions for future review",
      "Personal knowledge gaps",
      "Monthly case review",
      "Progress tracking",
      "Annual professional reflection",
    ],
    flow: false,
    note: "Instead of simply completing another case, you build a library of your own professional learning.",
    featured: false,
  },
];

export const tools = [
  { icon: FileTextIcon, title: "The 15-Minute Case Analysis Sheet", text: "A structured worksheet for practicing the complete case-review framework." },
  { icon: ClipboardCheckIcon, title: "The Pre-Case Review Checklist", text: "Organize the information you want to review before discussing a case." },
  { icon: MapIcon, title: "The Body Contouring Analysis Map", text: "A visual framework for organizing observations across the case." },
  { icon: Grid3x3Icon, title: "The Case Comparison Matrix", text: "Compare educational cases side by side and identify similarities, differences and learning points." },
  { icon: CameraIcon, title: "The Visual Case Documentation Guide", text: "Organize photographic and visual case documentation for educational review." },
];

export const bonuses = [
  {
    number: 1,
    title: "30 Educational Liposuction Cases",
    value: 47,
    image: "https://cdn.magicpatterns.com/uploads/1gnRx5nRc1JdJP3WfBDyis/BONUS_1_LIPO-EN.png",
    text: "A library of 30 additional educational case exercises designed to help you practice the framework repeatedly. Instead of reading another chapter, you'll have another case to analyze.",
    tagline: "Case → Think → Document → Review → Learn.",
  },
  {
    number: 2,
    title: "The 10-Minute Case Review™",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/eemxX737JNRa5MBwMHaumS/BONUS_2_LIPO-EN.png",
    text: "A compact version of the Case Lab framework for quickly reviewing an educational case. Use it when you don't have time to complete the full Case Lab.",
    tagline: "10 minutes. One case. One structured review.",
  },
  {
    number: 3,
    title: "The Liposuction Error Atlas",
    value: 47,
    image: "https://cdn.magicpatterns.com/uploads/c6Bnoawh2B1QUJBW6EKQyJ/BONUS_3_LIPO-EN.png",
    text: "Educational scenarios focused on common case-analysis mistakes and missed considerations: what happened, what was overlooked, questions to consider, review points and lessons learned.",
    tagline: "Don't memorize mistakes. Train yourself to look for them.",
  },
  {
    number: 4,
    title: "The Surgeon's Case Review Cards",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/hMnMaN54yEmsNLsSGkaYqX/BONUS_4_LIPO-EN.png",
    text: "Printable case-review prompts. Pull out a card, open a case, answer the question, review your reasoning. Use them for individual study, peer discussion or case conferences.",
    tagline: "One card. One question. Sharper reasoning.",
  },
  {
    number: 5,
    title: "The 30-Day Case Analysis Challenge",
    value: 29,
    image: "https://cdn.magicpatterns.com/uploads/nNDfR1LVjGbcURxwym4R8R/BONUS_5_LIPO-EN.png",
    text: "A 30-day professional practice calendar designed to turn case analysis into a consistent learning habit.",
    tagline: "One case. One exercise. One reflection. One lesson. Every day.",
  },
];

export const offerItems = [
  { label: "Liposuction Case Lab™ — 6 Premium Volumes", value: 97 },
  { label: "5 Professional Case Review Tools", value: 0 },
  { label: "Bonus #1 — 30 Educational Liposuction Cases", value: 47 },
  { label: "Bonus #2 — The 10-Minute Case Review™", value: 37 },
  { label: "Bonus #3 — The Liposuction Error Atlas", value: 47 },
  { label: "Bonus #4 — The Surgeon's Case Review Cards", value: 37 },
  { label: "Bonus #5 — The 30-Day Case Analysis Challenge", value: 29 },
];

export const guaranteeSteps = [
  "Open the six volumes",
  "Review the case framework",
  "Work through the educational cases",
  "Download the tools",
  "Explore the bonus materials",
];

export const before = [
  "You consume information passively.",
  "Every case feels like a completely new problem.",
  "Your case notes are inconsistent.",
  "You don't have a repeatable way to review your reasoning.",
  "You finish a case without systematically extracting the lessons.",
];

export const after = [
  "You have a structured case-review framework.",
  "You can work through an educational case in approximately 15 minutes.",
  "You have standardized worksheets for your case reviews.",
  "You can compare cases using the same framework.",
  "You can document what you learned from each case.",
];

export const testimonials = [
  {
    name: "Dr. Elena Rodríguez",
    role: "Plastic Surgeon",
    location: "Miami, USA",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/3452d760-a5ab-418e-acd1-1816c6767350.jpg",
    quote:
      "What I value most is the order. I open the analysis sheet before every consultation and I stopped jumping between notes. My case discussions with residents are far more structured now.",
  },
  {
    name: "Dr. James Whitaker",
    role: "Aesthetic Physician",
    location: "London, UK",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/6c0fd45f-8a9f-433e-bc14-ab86ebaeb90d.jpg",
    quote:
      "The case atlas made me realize how much I was skipping in my observation stage. Writing my priorities before reading the review is uncomfortable — and exactly why it works.",
  },
  {
    name: "Dr. Richard Hall",
    role: "Plastic Surgeon, 20+ years",
    location: "Toronto, Canada",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/b5c5a350-73da-454d-ab1f-c5a43bd68241.jpg",
    quote:
      "I've attended plenty of conferences. This is different: it's a tool I actually use. We now run the review cards in our monthly case conference.",
  },
  {
    name: "Dr. Grace Lin",
    role: "Aesthetic Medicine",
    location: "Sydney, Australia",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/220c83cb-5e11-4470-aae5-6c2baa3bf912.jpg",
    quote:
      "For $27 I expected a PDF. I got a complete system. The 30-day challenge turned case review into a daily habit I genuinely look forward to.",
  },
];

export const faqs = [
  { q: "How will I receive Liposuction Case Lab™?", a: "Immediately after your purchase, you'll receive access to the digital materials and downloadable resources included in the product." },
  { q: "Who is Liposuction Case Lab™ for?", a: "It's designed for qualified medical professionals seeking educational material for professional development in liposuction and body contouring case analysis." },
  { q: "Is this a surgical technique course?", a: "No. Liposuction Case Lab™ is a case-based educational and professional learning system. It does not replace surgical training, supervision, credentialing, institutional protocols or professional clinical judgment." },
  { q: "Does the product give me a step-by-step surgical protocol?", a: "No. The system focuses on structured case analysis, educational scenarios, documentation, reflection and professional learning. Clinical decisions should always be made by appropriately qualified professionals using current evidence, applicable standards and the specific circumstances of each patient." },
  { q: "How long does each case take?", a: "The core framework is designed so an educational case can be reviewed in approximately 15 minutes. More complex cases may naturally require more time." },
  { q: "Can I print the materials?", a: "Yes. The worksheets, checklists and case-review tools are designed to be used digitally or printed for personal professional study." },
  { q: "Is this a one-time payment?", a: "Yes. The launch offer is $27 as a one-time payment. There is no monthly subscription. Your local currency is converted automatically at checkout." },
  { q: "How long will I have access?", a: "The product is delivered as digital educational material with lifetime access under the purchase terms." },
  { q: "What happens if I don't like it?", a: "You have a 7-day guarantee. If it isn't right for your professional learning needs, request your refund within the guarantee period according to the purchase terms." },
];

export const buyers = [
  { name: "Dr. Alejandro M.", country: "Mexico" },
  { name: "Dra. Sofía R.", country: "Colombia" },
  { name: "Dr. Martín G.", country: "Argentina" },
  { name: "Dra. Lucía F.", country: "Spain" },
  { name: "Dr. Diego P.", country: "Chile" },
  { name: "Dra. Valentina C.", country: "Peru" },
  { name: "Dr. Andrés L.", country: "Venezuela" },
  { name: "Dra. Camila T.", country: "Ecuador" },
  { name: "Dr. José A.", country: "Dominican Republic" },
  { name: "Dra. Mariana S.", country: "Costa Rica" },
  { name: "Dr. Santiago V.", country: "Uruguay" },
  { name: "Dra. Gabriela H.", country: "Guatemala" },
  { name: "Dr. Ricardo N.", country: "Panama" },
  { name: "Dra. Isabel Q.", country: "Puerto Rico" },
  { name: "Dr. Fernando B.", country: "Bolivia" },
];
